/*
  Video testimonial slider
  ------------------------
  Custom element used by sections/video-testimonial-slider.liquid. No globals are created
  and no third-party slider is involved: everything lives on the element instance so the
  theme editor can load, unload and re-render the section as many times as it likes.

  Slider mechanics are the same architecture as assets/testimonial-slider.js so the theme
  keeps one slider pattern rather than two. Infinite loop: the last `perView` slides are
  cloned in front of the track and the first `perView` slides are cloned after it. The index
  is allowed to run one step past either end of the real range; when the transition finishes
  it is shifted back by the number of real slides and the transform is reapplied with the
  transition switched off, so the wrap is never visible.

  Added on top of that base, because video cards need it:
    - pointer drag / touch swipe, using the same pointer handling as
      assets/image-compare-slider.js (capture, movement threshold, dragstart preventDefault)
    - deferred playback: each card's <video> or <iframe> sits in an inert <template> and is
      only cloned into the DOM on click, so the page downloads no video bytes until asked
    - one video at a time: opening a card tears the previous player out of the DOM, which
      stops uploaded files and third-party iframes alike

  Videos are never started by this file on its own. There is no autoplay-on-load path, muted
  or otherwise; every play call is downstream of a click on a real <button>.
*/
(function () {
  var TAG = 'video-testimonial-slider';

  if (typeof window === 'undefined' || typeof customElements === 'undefined') return;
  if (customElements.get(TAG)) return;

  var REDUCED_MOTION = '(prefers-reduced-motion: reduce)';
  // Pointer travel, in px, before a press is treated as a swipe rather than a tap.
  var DRAG_THRESHOLD = 8;

  function VideoTestimonialSliderFactory() {
    return class VideoTestimonialSlider extends HTMLElement {
      constructor() {
        super();

        this.index = 0;
        this.step = 0;
        this.perView = 1;
        this.cloneCount = 0;
        this.slides = [];
        this.dots = [];
        this.active = false;
        this.built = false;
        this.isSliding = false;
        this.timer = null;
        this.settleTimer = null;
        this.announceReady = false;

        this.activeMedia = null;
        this.activePlayer = null;

        this.pointerId = null;
        this.dragging = false;
        this.dragStartX = 0;
        this.dragStartY = 0;
        this.dragBase = 0;
        this.suppressClick = false;

        this.pausedBy = {
          hover: false,
          focus: false,
          hidden: false,
          offscreen: false,
          editor: false,
          drag: false,
          video: false,
        };

        this.handleDot = this.handleDot.bind(this);
        this.handleTransitionEnd = this.handleTransitionEnd.bind(this);
        this.settle = this.settle.bind(this);
        this.handlePointerEnter = this.handlePointerEnter.bind(this);
        this.handlePointerLeave = this.handlePointerLeave.bind(this);
        this.handleFocusIn = this.handleFocusIn.bind(this);
        this.handleFocusOut = this.handleFocusOut.bind(this);
        this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
        this.handleMotionChange = this.handleMotionChange.bind(this);
        this.handleResize = this.handleResize.bind(this);
        this.handleIntersect = this.handleIntersect.bind(this);
        this.handleSectionLoad = this.handleSectionLoad.bind(this);
        this.handleSectionUnload = this.handleSectionUnload.bind(this);
        this.handleBlockSelect = this.handleBlockSelect.bind(this);
        this.handleBlockDeselect = this.handleBlockDeselect.bind(this);

        this.handleClick = this.handleClick.bind(this);
        this.handleClickCapture = this.handleClickCapture.bind(this);
        this.handlePointerDown = this.handlePointerDown.bind(this);
        this.handlePointerMove = this.handlePointerMove.bind(this);
        this.handlePointerUp = this.handlePointerUp.bind(this);
        this.handleDragStart = this.handleDragStart.bind(this);
      }

      /* ---------- lifecycle ---------- */

      connectedCallback() {
        this.viewport = this.querySelector('[data-vts-viewport]');
        this.track = this.querySelector('[data-vts-track]');
        if (!this.viewport || !this.track) return;

        this.slides = Array.prototype.filter.call(this.track.children, function (child) {
          return child.hasAttribute('data-vts-slide') && !child.hasAttribute('data-vts-clone');
        });
        if (!this.slides.length) return;

        this.status = this.querySelector('[data-vts-status]');
        this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-vts-dot]'));
        this.dots.forEach(
          function (dot) {
            dot.addEventListener('click', this.handleDot);
          }.bind(this)
        );

        // Delegated so a card added or re-rendered by the editor is picked up for free.
        this.viewport.addEventListener('click', this.handleClick);
        this.viewport.addEventListener('click', this.handleClickCapture, true);
        this.viewport.addEventListener('pointerdown', this.handlePointerDown);
        this.viewport.addEventListener('pointermove', this.handlePointerMove);
        this.viewport.addEventListener('pointerup', this.handlePointerUp);
        this.viewport.addEventListener('pointercancel', this.handlePointerUp);
        // Belt and braces with preventDefault() on pointermove: a native image drag steals
        // the pointermove stream and leaves the track stuck mid-swipe.
        this.viewport.addEventListener('dragstart', this.handleDragStart);

        this.track.addEventListener('transitionend', this.handleTransitionEnd);
        this.addEventListener('mouseenter', this.handlePointerEnter);
        this.addEventListener('mouseleave', this.handlePointerLeave);
        this.addEventListener('focusin', this.handleFocusIn);
        this.addEventListener('focusout', this.handleFocusOut);
        document.addEventListener('visibilitychange', this.handleVisibilityChange);

        if (window.matchMedia) {
          this.motionQuery = window.matchMedia(REDUCED_MOTION);
          if (this.motionQuery.addEventListener) {
            this.motionQuery.addEventListener('change', this.handleMotionChange);
          } else if (this.motionQuery.addListener) {
            this.motionQuery.addListener(this.handleMotionChange);
          }
        }

        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(this.handleResize);
          this.resizeObserver.observe(this.viewport);
        } else {
          window.addEventListener('resize', this.handleResize);
        }

        if ('IntersectionObserver' in window) {
          this.intersectionObserver = new IntersectionObserver(this.handleIntersect, { threshold: 0 });
          this.intersectionObserver.observe(this);
        }

        document.addEventListener('shopify:section:load', this.handleSectionLoad);
        document.addEventListener('shopify:section:unload', this.handleSectionUnload);
        document.addEventListener('shopify:block:select', this.handleBlockSelect);
        document.addEventListener('shopify:block:deselect', this.handleBlockDeselect);

        this.pausedBy.hidden = document.hidden === true;
        this.rebuild();
      }

      disconnectedCallback() {
        this.destroy();
      }

      destroy() {
        this.stopAutoplay();
        this.clearSettleFallback();
        this.closeVideo();
        this.endDrag(false);
        this.isSliding = false;

        this.dots.forEach(
          function (dot) {
            dot.removeEventListener('click', this.handleDot);
          }.bind(this)
        );

        if (this.viewport) {
          this.viewport.removeEventListener('click', this.handleClick);
          this.viewport.removeEventListener('click', this.handleClickCapture, true);
          this.viewport.removeEventListener('pointerdown', this.handlePointerDown);
          this.viewport.removeEventListener('pointermove', this.handlePointerMove);
          this.viewport.removeEventListener('pointerup', this.handlePointerUp);
          this.viewport.removeEventListener('pointercancel', this.handlePointerUp);
          this.viewport.removeEventListener('dragstart', this.handleDragStart);
        }

        if (this.track) this.track.removeEventListener('transitionend', this.handleTransitionEnd);
        this.removeEventListener('mouseenter', this.handlePointerEnter);
        this.removeEventListener('mouseleave', this.handlePointerLeave);
        this.removeEventListener('focusin', this.handleFocusIn);
        this.removeEventListener('focusout', this.handleFocusOut);
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);

        if (this.motionQuery) {
          if (this.motionQuery.removeEventListener) {
            this.motionQuery.removeEventListener('change', this.handleMotionChange);
          } else if (this.motionQuery.removeListener) {
            this.motionQuery.removeListener(this.handleMotionChange);
          }
          this.motionQuery = null;
        }

        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
          this.resizeObserver = null;
        } else {
          window.removeEventListener('resize', this.handleResize);
        }

        if (this.intersectionObserver) {
          this.intersectionObserver.disconnect();
          this.intersectionObserver = null;
        }

        document.removeEventListener('shopify:section:load', this.handleSectionLoad);
        document.removeEventListener('shopify:section:unload', this.handleSectionUnload);
        document.removeEventListener('shopify:block:select', this.handleBlockSelect);
        document.removeEventListener('shopify:block:deselect', this.handleBlockDeselect);

        this.removeClones();
        if (this.track) {
          this.track.style.transitionDuration = '';
          this.track.style.transform = '';
        }
        this.built = false;
      }

      /* ---------- settings ---------- */

      get autoplayEnabled() {
        return this.dataset.autoplay === 'true';
      }

      get autoplayDelay() {
        var seconds = parseFloat(this.dataset.autoplaySpeed);
        if (!seconds || seconds < 1) seconds = 6;
        return seconds * 1000;
      }

      get reducedMotion() {
        return this.motionQuery ? this.motionQuery.matches : false;
      }

      /* Reads the cards-per-view value straight out of the computed custom property, so
         CSS media queries stay the single source of truth for the breakpoints. */
      readPerView() {
        var raw = window.getComputedStyle(this.track).getPropertyValue('--vts-per-view');
        var value = parseInt(raw, 10);
        if (!value || value < 1) value = 1;
        return Math.min(value, 4);
      }

      /* ---------- build ---------- */

      rebuild() {
        if (!this.track || !this.slides.length) return;

        var perView = this.readPerView();
        var needsClones = perView !== this.perView || !this.built;

        this.perView = perView;
        this.active = this.slides.length > perView;

        // Never clone a card mid-playback: the copy would be a second live player.
        if (needsClones) {
          this.closeVideo();
          this.buildClones();
        }
        this.built = true;

        if (!this.active) this.index = 0;

        this.toggleAttribute('data-vts-static', !this.active);
        this.measure();
        this.render(false);
        this.updateAutoplay();
      }

      removeClones() {
        if (!this.track) return;
        var clones = this.track.querySelectorAll('[data-vts-clone]');
        Array.prototype.forEach.call(clones, function (clone) {
          clone.parentNode.removeChild(clone);
        });
        this.cloneCount = 0;
      }

      buildClones() {
        this.removeClones();
        if (!this.active) return;

        var count = Math.min(this.perView, this.slides.length);
        var i;

        for (i = 0; i < count; i++) {
          // Leading clones mirror the tail, inserted in reverse so order is preserved.
          this.track.insertBefore(this.makeClone(this.slides[this.slides.length - 1 - i]), this.track.firstChild);
          this.track.appendChild(this.makeClone(this.slides[i]));
        }

        this.cloneCount = count;
      }

      makeClone(slide) {
        var clone = slide.cloneNode(true);
        clone.setAttribute('data-vts-clone', '');
        clone.setAttribute('aria-hidden', 'true');

        // A clone is only ever on screen mid-transition, so its play button must not be
        // reachable by keyboard or pointer — an interactive control inside aria-hidden
        // content is a genuine a11y fault, not a nicety.
        clone.inert = true;
        clone.setAttribute('inert', '');
        Array.prototype.forEach.call(clone.querySelectorAll('button, a'), function (node) {
          node.setAttribute('tabindex', '-1');
        });

        // Strip the theme editor hooks so a clone can never be mistaken for a real block.
        Array.prototype.slice.call(clone.attributes).forEach(function (attribute) {
          if (attribute.name.indexOf('data-shopify-editor') === 0) {
            clone.removeAttribute(attribute.name);
          }
        });
        return clone;
      }

      measure() {
        var slide = this.slides[0];
        if (!slide) return;

        var styles = window.getComputedStyle(this.track);
        var gap = parseFloat(styles.columnGap || styles.gap || '0');
        if (isNaN(gap)) gap = 0;

        this.step = slide.getBoundingClientRect().width + gap;
      }

      /* ---------- movement ---------- */

      baseOffset() {
        return this.active ? -(this.index + this.cloneCount) * this.step : 0;
      }

      render(animate) {
        if (!this.track) return false;

        var shouldAnimate = animate && !this.reducedMotion && this.step > 0;
        var transform = 'translate3d(' + this.baseOffset() + 'px, 0, 0)';

        if (shouldAnimate) {
          this.isSliding = true;
          this.armSettleFallback();
          this.track.style.transform = transform;
        } else {
          this.isSliding = false;
          this.clearSettleFallback();
          this.track.style.transitionDuration = '0ms';
          this.track.style.transform = transform;
          // Force a reflow so the jump is committed before transitions come back.
          this.track.getBoundingClientRect();
          this.track.style.transitionDuration = '';
        }

        this.updateDots();
        return shouldAnimate;
      }

      /* transitionend is the normal settle trigger; this covers the cases where it never
         fires (a zero-length transition, a tab hidden mid-slide, an unchanged transform). */
      armSettleFallback() {
        this.clearSettleFallback();
        var duration = parseFloat(window.getComputedStyle(this.track).transitionDuration) || 0.5;
        this.settleTimer = window.setTimeout(this.settle, duration * 1000 + 120);
      }

      clearSettleFallback() {
        if (!this.settleTimer) return;
        window.clearTimeout(this.settleTimer);
        this.settleTimer = null;
      }

      settle() {
        this.clearSettleFallback();
        this.isSliding = false;
        this.normalize();
      }

      /* Pulls an out-of-range index back into the real range without any visible move. */
      normalize() {
        var total = this.slides.length;
        if (this.index >= total) {
          this.index -= total;
        } else if (this.index < 0) {
          this.index += total;
        } else {
          return;
        }
        this.render(false);
      }

      move(delta) {
        if (!this.active || this.isSliding) return;
        this.announceReady = true;
        this.index += delta;
        var animated = this.render(true);
        if (!animated) this.normalize();
      }

      goToIndex(target) {
        if (!this.active || this.isSliding) return;
        if (target === this.realIndex()) return;
        this.announceReady = true;
        this.index = target;
        this.render(true);
      }

      realIndex() {
        var total = this.slides.length;
        return ((this.index % total) + total) % total;
      }

      updateDots() {
        var current = this.realIndex();

        this.dots.forEach(function (dot, position) {
          var dotIndex = parseInt(dot.dataset.vtsDot, 10);
          if (isNaN(dotIndex)) dotIndex = position;
          if (dotIndex === current) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });

        this.updateStatus(current);
      }

      /* Rewrites the live region so a screen reader hears which card is now in front.
         Held back until the first real navigation, so simply loading the page — or the
         theme editor re-rendering the section — never announces anything. */
      updateStatus(current) {
        if (!this.status || !this.active) return;
        if (!this.announceReady) return;

        var text = 'Video testimonial ' + (current + 1) + ' of ' + this.slides.length;
        if (this.status.textContent === text) return;
        this.status.textContent = text;
      }

      /* ---------- video ---------- */

      /* Swaps a card's poster for its real player. The player markup is cloned out of an
         inert <template>, which is why nothing has been fetched up to this point. */
      openVideo(button) {
        var media = button.closest('[data-vts-media]');
        if (!media) return;

        var kind = button.dataset.vtsKind;

        // A source we could not classify: hand over the original link rather than guess at
        // an embed URL that would fail silently inside the card.
        if (kind === 'link') {
          var href = button.dataset.vtsHref;
          if (href) window.open(href, '_blank', 'noopener,noreferrer');
          return;
        }

        var template = media.querySelector('[data-vts-template]');
        if (!template) return;

        if (this.activeMedia === media) return;
        this.closeVideo();

        var player = template.content.firstElementChild;
        if (!player) return;
        player = player.cloneNode(true);

        button.hidden = true;
        media.appendChild(player);

        this.activeMedia = media;
        this.activePlayer = player;

        // Hiding the button the visitor just activated drops focus to <body>. Hand it to the
        // player instead, so a keyboard user lands on the controls they asked for.
        player.setAttribute('tabindex', '-1');
        try {
          player.focus({ preventScroll: true });
        } catch (error) {
          // preventScroll is unsupported on older Safari; the focus itself is optional.
        }

        if (typeof player.play === 'function') {
          var started = player.play();
          // Some browsers reject an unmuted play() even after a gesture. The controls are
          // already on screen, so let the visitor press play rather than throw.
          if (started && typeof started.catch === 'function') started.catch(function () {});
        }

        // Slides must not cycle out from under a playing video.
        this.pause('video');
      }

      /* Tearing the node out is what actually stops playback — it works the same for an
         uploaded file and for a YouTube or Vimeo iframe we have no API access to. */
      closeVideo() {
        if (!this.activeMedia) return;

        // Removing a focused node strands focus on <body>, so note whether we owned it
        // before the player goes away.
        var hadFocus = this.activeMedia.contains(document.activeElement);

        if (this.activePlayer && this.activePlayer.parentNode) {
          if (typeof this.activePlayer.pause === 'function') this.activePlayer.pause();
          this.activePlayer.parentNode.removeChild(this.activePlayer);
        }

        var poster = this.activeMedia.querySelector('[data-vts-play]');
        if (poster) {
          poster.hidden = false;
          if (hadFocus) {
            try {
              poster.focus({ preventScroll: true });
            } catch (error) {
              poster.focus();
            }
          }
        }

        this.activeMedia = null;
        this.activePlayer = null;
        this.resume('video');
      }

      /* ---------- drag ---------- */

      handlePointerDown(event) {
        if (!this.active || this.isSliding) return;
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        // Never hijack a gesture aimed at video controls.
        if (event.target.closest('video, iframe')) return;

        // A drag that ended without producing a click (released outside the viewport, say)
        // would otherwise leave the flag armed and swallow the next genuine press.
        this.suppressClick = false;

        this.pointerId = event.pointerId;
        this.dragging = false;
        this.dragStartX = event.clientX;
        this.dragStartY = event.clientY;
        this.dragBase = this.baseOffset();
      }

      handlePointerMove(event) {
        if (this.pointerId === null || event.pointerId !== this.pointerId) return;

        var dx = event.clientX - this.dragStartX;
        var dy = event.clientY - this.dragStartY;

        if (!this.dragging) {
          // Only claim the gesture once it is clearly horizontal, so a vertical page scroll
          // that starts on a card still scrolls the page.
          if (Math.abs(dx) < DRAG_THRESHOLD || Math.abs(dx) <= Math.abs(dy)) return;

          this.dragging = true;
          this.setAttribute('data-vts-dragging', '');
          this.pause('drag');
          if (this.viewport.setPointerCapture) {
            try {
              this.viewport.setPointerCapture(this.pointerId);
            } catch (error) {
              // Capture is a nicety; the gesture still works through the listeners below.
            }
          }
        }

        event.preventDefault();
        this.track.style.transform = 'translate3d(' + (this.dragBase + dx) + 'px, 0, 0)';
      }

      handlePointerUp(event) {
        if (this.pointerId === null || event.pointerId !== this.pointerId) return;

        var dx = this.dragging ? event.clientX - this.dragStartX : 0;
        var wasDragging = this.dragging;
        this.endDrag(true);

        if (!wasDragging) return;

        // A swipe counts once it clears a fifth of a card, with a floor so a narrow card on
        // a phone still needs a deliberate movement.
        var threshold = Math.max(DRAG_THRESHOLD * 5, this.step * 0.2);
        if (dx <= -threshold) {
          this.move(1);
        } else if (dx >= threshold) {
          this.move(-1);
        } else {
          this.render(true);
        }
      }

      endDrag(suppressClick) {
        if (this.pointerId !== null && this.viewport && this.viewport.releasePointerCapture) {
          try {
            if (this.viewport.hasPointerCapture && this.viewport.hasPointerCapture(this.pointerId)) {
              this.viewport.releasePointerCapture(this.pointerId);
            }
          } catch (error) {
            // Already released, nothing to do.
          }
        }

        // The click that ends a swipe would otherwise land on whichever play button the
        // finger came to rest over.
        this.suppressClick = suppressClick === true && this.dragging;

        if (this.dragging) {
          this.dragging = false;
          this.removeAttribute('data-vts-dragging');
          this.resume('drag');
        }

        this.pointerId = null;
      }

      handleDragStart(event) {
        event.preventDefault();
      }

      handleClickCapture(event) {
        if (!this.suppressClick) return;
        this.suppressClick = false;
        event.preventDefault();
        event.stopPropagation();
      }

      handleClick(event) {
        var button = event.target.closest('[data-vts-play]');
        if (!button || !this.viewport.contains(button)) return;
        this.openVideo(button);
      }

      /* ---------- autoplay ---------- */

      get shouldAutoplay() {
        if (!this.autoplayEnabled || !this.active || this.reducedMotion) return false;
        var flags = this.pausedBy;
        return !(
          flags.hover ||
          flags.focus ||
          flags.hidden ||
          flags.offscreen ||
          flags.editor ||
          flags.drag ||
          flags.video
        );
      }

      updateAutoplay() {
        if (this.shouldAutoplay) {
          this.startAutoplay();
        } else {
          this.stopAutoplay();
        }
        this.updateLiveRegion();
      }

      /* Announcing every slide of a self-cycling carousel is noise; announcing a slide the
         visitor asked for is useful. Same trade-off Dawn makes in its own slider. */
      updateLiveRegion() {
        if (!this.status) return;
        this.status.setAttribute('aria-live', this.timer ? 'off' : 'polite');
      }

      startAutoplay() {
        if (this.timer) return;
        this.timer = window.setInterval(
          function () {
            this.move(1);
          }.bind(this),
          this.autoplayDelay
        );
      }

      stopAutoplay() {
        if (!this.timer) return;
        window.clearInterval(this.timer);
        this.timer = null;
      }

      pause(reason) {
        this.pausedBy[reason] = true;
        this.updateAutoplay();
      }

      resume(reason) {
        this.pausedBy[reason] = false;
        this.updateAutoplay();
      }

      /* ---------- handlers ---------- */

      handleDot(event) {
        var target = parseInt(event.currentTarget.dataset.vtsDot, 10);
        if (isNaN(target)) return;
        this.goToIndex(target);
      }

      handleTransitionEnd(event) {
        if (event.target !== this.track || event.propertyName !== 'transform') return;
        this.settle();
      }

      handlePointerEnter() {
        this.pause('hover');
      }

      handlePointerLeave() {
        this.resume('hover');
      }

      handleFocusIn() {
        this.pause('focus');
      }

      handleFocusOut(event) {
        if (event.relatedTarget && this.contains(event.relatedTarget)) return;
        this.resume('focus');
      }

      handleVisibilityChange() {
        if (document.hidden) {
          this.pause('hidden');
        } else {
          this.resume('hidden');
        }
      }

      handleMotionChange() {
        this.render(false);
        this.normalize();
        this.updateAutoplay();
      }

      handleResize() {
        this.rebuild();
      }

      handleIntersect(entries) {
        var entry = entries[entries.length - 1];
        if (!entry) return;
        if (entry.isIntersecting) {
          this.resume('offscreen');
        } else {
          // Scrolled away mid-playback: stop the audio rather than leave it running offscreen.
          this.closeVideo();
          this.pause('offscreen');
        }
      }

      /* ---------- theme editor ---------- */

      handleSectionLoad(event) {
        if (!event.target.contains(this)) return;
        this.built = false;
        this.rebuild();
      }

      handleSectionUnload(event) {
        if (!event.target.contains(this)) return;
        this.destroy();
      }

      handleBlockSelect(event) {
        if (!this.contains(event.target)) return;
        var slide = event.target.closest('[data-vts-slide]');
        if (slide) {
          var position = this.slides.indexOf(slide);
          if (position > -1) this.goToIndex(position);
        }
        this.pause('editor');
      }

      handleBlockDeselect(event) {
        if (!this.contains(event.target)) return;
        this.resume('editor');
      }
    };
  }

  customElements.define(TAG, VideoTestimonialSliderFactory());
})();
